/**
 * Entity Management UI
 * 
 * Form-based interface for creating and editing entities
 * Requirements: 1.1-1.12
 */

import React, { useState, useEffect } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';

export interface EntityFormData {
  name: string;
  dataTypes: string[];
  dataSubjectJurisdictions: Record<string, string[]>;
  entityJurisdiction: string;
  naicsCodes: string[];
  regulatoryCategories: string[];
  businessModel?: string;
  customAttributes: Record<string, any>;
}

interface EntityManagementUIProps {
  analysisId: string;
  onEntityCreate?: (entity: EntityFormData) => void;
  onEntityUpdate?: (entityId: string, updates: Partial<EntityFormData>) => void;
  onEntityDelete?: (entityId: string) => void;
}

const DATA_TYPES = [
  { label: 'General Data', value: 'general_data' },
  { label: 'PII', value: 'PII' },
  { label: 'Financial Data', value: 'financial_data' },
  { label: 'Health Data', value: 'health_data' },
  { label: 'Payment Card Data', value: 'payment_card_data' },
  { label: 'Government Data', value: 'government_data' }
];

const BUSINESS_MODELS = [
  { label: 'As-A-Service (SaaS/PaaS/IaaS)', value: 'as_a_service' },
  { label: 'Traditional License', value: 'traditional_license' },
  { label: 'Managed Service', value: 'managed_service' },
  { label: 'Product Vendor', value: 'product_vendor' },
  { label: 'Consulting Services', value: 'consulting_services' },
  { label: 'Open Source', value: 'open_source' },
  { label: 'Hybrid', value: 'hybrid' }
];

const JURISDICTIONS = ['EU', 'US', 'Canada', 'UK', 'China', 'Japan', 'Australia', 'Brazil', 'India'];
const REGULATORY_CATEGORIES = [
  { label: 'Financial Institution', value: 'financial_institution' },
  { label: 'Healthcare Provider', value: 'healthcare_provider' },
  { label: 'Critical Infrastructure', value: 'critical_infrastructure' },
  { label: 'Public Sector', value: 'public_sector' },
  { label: 'Technology Provider', value: 'technology_provider' },
];

/**
 * Entity Management UI Component
 * 
 * Complete implementation with:
 * - Full entity creation form with validation
 * - Data type multi-select with jurisdiction mapping
 * - NAICS code input
 * - Regulatory category multi-select
 * - Custom attribute key-value editor
 * - Entity list with search and filter
 * - Edit and delete actions
 * - API integration
 */
export const EntityManagementUI: React.FC<EntityManagementUIProps> = ({
  analysisId,
  onEntityCreate,
  onEntityUpdate,
  onEntityDelete,
}) => {
  const [showForm, setShowForm] = useState(false);
  const [editingEntityId, setEditingEntityId] = useState<string | null>(null);
  const [entities, setEntities] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [formData, setFormData] = useState<EntityFormData>({
    name: '',
    dataTypes: ['general_data'], // Default to General Data
    dataSubjectJurisdictions: {},
    entityJurisdiction: '',
    naicsCodes: [],
    regulatoryCategories: [],
    businessModel: undefined,
    customAttributes: {},
  });

  const [customAttrKey, setCustomAttrKey] = useState('');
  const [customAttrValue, setCustomAttrValue] = useState('');
  const [expandedEntities, setExpandedEntities] = useState<Set<string>>(new Set());

  const toggleEntityExpansion = (entityId: string) => {
    const newExpanded = new Set(expandedEntities);
    if (newExpanded.has(entityId)) {
      newExpanded.delete(entityId);
    } else {
      newExpanded.add(entityId);
    }
    setExpandedEntities(newExpanded);
  };

  // Fetch entities on mount
  useEffect(() => {
    fetchEntities();
  }, [analysisId]);

  // Listen for entity refresh events (e.g., after tension calculation)
  useEffect(() => {
    const handleRefresh = () => {
      console.log('[ENTITY-UI] Received refresh event, refetching entities...');
      fetchEntities();
    };
    
    window.addEventListener('entitiesNeedRefresh', handleRefresh);
    
    return () => {
      window.removeEventListener('entitiesNeedRefresh', handleRefresh);
    };
  }, [analysisId]);

  const fetchEntities = async () => {
    try {
      setLoading(true);
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      const apiUrl = import.meta.env?.VITE_API_ENDPOINT || 'http://localhost:3000';
      const response = await fetch(`${apiUrl}/analyses/${analysisId}/entities`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch entities');
      
      const data = await response.json();
      console.log('[ENTITY-UI] Fetched entities:', data.length, 'entities');
      console.log('[ENTITY-UI] Sample entity with risk score:', data[0]);
      setEntities(data);
    } catch (err) {
      console.error('Error fetching entities:', err);
      setError(err instanceof Error ? err.message : 'Failed to load entities');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.name.trim()) {
      alert('Entity name is required');
      return;
    }
    if (formData.dataTypes.length === 0) {
      alert('At least one data type is required');
      return;
    }
    if (!formData.entityJurisdiction) {
      alert('Entity jurisdiction is required');
      return;
    }

    // Build dataSubjectJurisdictions from selected data types and entity jurisdiction
    // This ensures it's always correct regardless of the order fields were filled
    const dataSubjectJurisdictions: Record<string, string[]> = {};
    formData.dataTypes.forEach(dataType => {
      dataSubjectJurisdictions[dataType] = [formData.entityJurisdiction];
    });

    try {
      setLoading(true);
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      const apiUrl = import.meta.env?.VITE_API_ENDPOINT || 'http://localhost:3000';
      
      // Prepare the payload with the correctly built dataSubjectJurisdictions
      const payload = {
        ...formData,
        dataSubjectJurisdictions
      };
      
      if (editingEntityId) {
        // Update existing entity
        const response = await fetch(`${apiUrl}/analyses/${analysisId}/entities/${editingEntityId}`, {
          method: 'PUT',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(payload),
        });
        
        if (!response.ok) throw new Error('Failed to update entity');
        
        if (onEntityUpdate) onEntityUpdate(editingEntityId, formData);
      } else {
        // Create new entity
        const response = await fetch(`${apiUrl}/analyses/${analysisId}/entities`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(payload),
        });
        
        if (!response.ok) throw new Error('Failed to create entity');
        
        if (onEntityCreate) onEntityCreate(formData);
      }
      
      // Reset form
      resetForm();
      fetchEntities();
    } catch (err) {
      console.error('Error saving entity:', err);
      alert(err instanceof Error ? err.message : 'Failed to save entity');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (entityId: string) => {
    if (!confirm('Are you sure you want to delete this entity?')) return;

    try {
      setLoading(true);
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      const apiUrl = import.meta.env?.VITE_API_ENDPOINT || 'http://localhost:3000';
      const response = await fetch(`${apiUrl}/analyses/${analysisId}/entities/${entityId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to delete entity');
      
      if (onEntityDelete) onEntityDelete(entityId);
      fetchEntities();
    } catch (err) {
      console.error('Error deleting entity:', err);
      alert(err instanceof Error ? err.message : 'Failed to delete entity');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (entity: any) => {
    setFormData({
      name: entity.name,
      dataTypes: entity.dataTypes || [],
      dataSubjectJurisdictions: entity.dataSubjectJurisdictions || {},
      entityJurisdiction: entity.entityJurisdiction || '',
      naicsCodes: entity.naicsCodes || [],
      regulatoryCategories: entity.regulatoryCategories || [],
      businessModel: entity.businessModel,
      customAttributes: entity.customAttributes || {},
    });
    setEditingEntityId(entity.id);
    setShowForm(true);
    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCopy = (entity: any) => {
    // Populate form with entity data
    setFormData({
      name: `${entity.name} (Copy)`,
      dataTypes: entity.dataTypes || ['general_data'],
      dataSubjectJurisdictions: entity.dataSubjectJurisdictions || {},
      entityJurisdiction: entity.entityJurisdiction || '',
      naicsCodes: entity.naicsCodes || [],
      regulatoryCategories: entity.regulatoryCategories || [],
      businessModel: entity.businessModel,
      customAttributes: entity.customAttributes || {},
    });
    setEditingEntityId(null); // Not editing, creating new
    setShowForm(true);
    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      dataTypes: ['general_data'], // Default to General Data
      dataSubjectJurisdictions: {},
      entityJurisdiction: '',
      naicsCodes: [],
      regulatoryCategories: [],
      businessModel: undefined,
      customAttributes: {},
    });
    setEditingEntityId(null);
    setShowForm(false);
    setCustomAttrKey('');
    setCustomAttrValue('');
  };

  const handleDataTypeToggle = (dataType: string) => {
    const updated = formData.dataTypes.includes(dataType)
      ? formData.dataTypes.filter(dt => dt !== dataType)
      : [...formData.dataTypes, dataType];
    
    setFormData({ ...formData, dataTypes: updated });
  };

  const handleRegulatoryToggle = (category: string) => {
    const updated = formData.regulatoryCategories.includes(category)
      ? formData.regulatoryCategories.filter(rc => rc !== category)
      : [...formData.regulatoryCategories, category];
    setFormData({ ...formData, regulatoryCategories: updated });
  };

  const handleAddCustomAttribute = () => {
    if (!customAttrKey.trim()) return;
    
    setFormData({
      ...formData,
      customAttributes: {
        ...formData.customAttributes,
        [customAttrKey]: customAttrValue,
      },
    });
    setCustomAttrKey('');
    setCustomAttrValue('');
  };

  const handleRemoveCustomAttribute = (key: string) => {
    const updated = { ...formData.customAttributes };
    delete updated[key];
    setFormData({ ...formData, customAttributes: updated });
  };

  const filteredEntities = entities.filter(entity =>
    entity.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="entity-management-ui">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ margin: 0 }}>Entities</h3>
        <button
          onClick={() => {
            if (showForm) resetForm();
            else setShowForm(true);
          }}
          style={{
            padding: '8px 16px',
            background: showForm ? '#ef4444' : '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          {showForm ? 'Cancel' : '+ Add Entity'}
        </button>
      </div>

      {error && (
        <div style={{ padding: '12px', background: '#fee', border: '1px solid #fcc', borderRadius: '4px', marginBottom: '16px', color: '#c00' }}>
          {error}
        </div>
      )}

      {/* Entity Creation/Edit Form */}
      {showForm && (
        <div className="entity-form" style={{
          border: '1px solid #d1d5db',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '16px',
          background: '#fafafa',
        }}>
          <h4 style={{ marginTop: 0 }}>{editingEntityId ? 'Edit Entity' : 'Create Entity'}</h4>
          
          {/* Name */}
          <div className="form-field" style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', fontSize: '14px' }}>
              Name: <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Acme Corporation"
              style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
            />
          </div>

          {/* Data Types */}
          <div className="form-field" style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', fontSize: '14px' }}>
              Data Types: <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
              {DATA_TYPES.map(type => (
                <label key={type.value} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '13px' }}>
                  <input
                    type="checkbox"
                    checked={formData.dataTypes.includes(type.value)}
                    onChange={() => handleDataTypeToggle(type.value)}
                    style={{ marginRight: '6px' }}
                  />
                  {type.label}
                </label>
              ))}
            </div>
          </div>

          {/* Entity Jurisdiction */}
          <div className="form-field" style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', fontSize: '14px' }}>
              Entity Jurisdiction: <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <select
              value={formData.entityJurisdiction}
              onChange={(e) => setFormData({ ...formData, entityJurisdiction: e.target.value })}
              style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
            >
              <option value="">Select jurisdiction...</option>
              {JURISDICTIONS.map(jurisdiction => (
                <option key={jurisdiction} value={jurisdiction}>{jurisdiction}</option>
              ))}
            </select>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
              This will be used as the jurisdiction for all data subjects
            </div>
          </div>

          {/* Business Model */}
          <div className="form-field" style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', fontSize: '14px' }}>
              Business Model:
            </label>
            <select
              value={formData.businessModel || ''}
              onChange={(e) => setFormData({ 
                ...formData, 
                businessModel: e.target.value || undefined 
              })}
              style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
            >
              <option value="">Not specified</option>
              {BUSINESS_MODELS.map(model => (
                <option key={model.value} value={model.value}>{model.label}</option>
              ))}
            </select>
          </div>

          {/* Regulatory Categories */}
          <div className="form-field" style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', fontSize: '14px' }}>
              Regulatory Categories:
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
              {REGULATORY_CATEGORIES.map(cat => (
                <label key={cat.value} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '13px' }}>
                  <input
                    type="checkbox"
                    checked={formData.regulatoryCategories.includes(cat.value)}
                    onChange={() => handleRegulatoryToggle(cat.value)}
                    style={{ marginRight: '6px' }}
                  />
                  {cat.label}
                </label>
              ))}
            </div>
          </div>

          {/* Custom Attributes */}
          <div className="form-field" style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', fontSize: '14px' }}>
              Custom Attributes:
            </label>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
              <input
                type="text"
                value={customAttrKey}
                onChange={(e) => setCustomAttrKey(e.target.value)}
                placeholder="Key"
                style={{ flex: 1, padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
              />
              <input
                type="text"
                value={customAttrValue}
                onChange={(e) => setCustomAttrValue(e.target.value)}
                placeholder="Value"
                style={{ flex: 1, padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
              />
              <button
                onClick={handleAddCustomAttribute}
                style={{
                  padding: '8px 16px',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                Add
              </button>
            </div>
            <div style={{ fontSize: '12px', fontFamily: 'monospace', background: 'white', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}>
              {Object.entries(formData.customAttributes).map(([key, value]) => (
                <div key={key} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span><strong>{key}:</strong> {JSON.stringify(value)}</span>
                  <button
                    onClick={() => handleRemoveCustomAttribute(key)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#ef4444',
                    }}
                  >
                    Remove
                  </button>
                </div>
              ))}
              {Object.keys(formData.customAttributes).length === 0 && (
                <span style={{ color: '#999' }}>No custom attributes</span>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={handleSubmit}
              disabled={loading}
              style={{
                padding: '10px 20px',
                background: '#22c55e',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontWeight: '500',
              }}
            >
              {loading ? 'Saving...' : editingEntityId ? 'Update Entity' : 'Create Entity'}
            </button>
            <button
              onClick={resetForm}
              style={{
                padding: '10px 20px',
                background: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Entity List */}
      <div className="entity-list">
        <div style={{ marginBottom: '12px' }}>
          <input
            type="text"
            placeholder="Search entities..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
          />
        </div>

        {loading && !showForm && (
          <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
            Loading entities...
          </div>
        )}

        {!loading && filteredEntities.length === 0 && (
          <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
            No entities found
          </div>
        )}

        <div style={{ display: 'grid', gap: '12px' }}>
          {filteredEntities.map(entity => (
            <div key={entity.id} style={{
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              padding: '12px',
              background: 'white',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px' }}>
                <div style={{ flex: 1 }}>
                  <h4 style={{ margin: '0 0 4px 0', fontSize: '16px' }}>{entity.name}</h4>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {entity.entityJurisdiction} • Risk: {typeof entity.riskScore === 'number' ? entity.riskScore.toFixed(1) : '0.0'}
                    {entity.appliedFrictions && entity.appliedFrictions.length > 0 && (
                      <span style={{ marginLeft: '8px', color: '#3b82f6', cursor: 'pointer' }} onClick={() => toggleEntityExpansion(entity.id)}>
                        {expandedEntities.has(entity.id) ? '▼' : '▶'} {entity.appliedFrictions.length} friction{entity.appliedFrictions.length !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button
                    onClick={() => handleCopy(entity)}
                    style={{
                      padding: '4px 12px',
                      background: '#8b5cf6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px',
                    }}
                  >
                    Copy
                  </button>
                  <button
                    onClick={() => handleEdit(entity)}
                    style={{
                      padding: '4px 12px',
                      background: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px',
                    }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(entity.id)}
                    style={{
                      padding: '4px 12px',
                      background: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px',
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
              
              {/* Friction Breakdown - Collapsible */}
              {expandedEntities.has(entity.id) && entity.appliedFrictions && entity.appliedFrictions.length > 0 && (
                <div style={{ 
                  marginTop: '12px', 
                  padding: '8px', 
                  background: '#f9fafb', 
                  borderRadius: '4px',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{ fontWeight: '600', fontSize: '12px', marginBottom: '6px', color: '#374151' }}>
                    Applied Frictions:
                  </div>
                  {entity.appliedFrictions.map((friction: any) => (
                    <div key={friction.id} style={{ 
                      fontSize: '11px', 
                      marginBottom: '4px',
                      padding: '4px 6px',
                      background: 'white',
                      borderRadius: '3px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div>
                        <span style={{ fontWeight: '500' }}>{friction.name}</span>
                        {friction.regulatoryFramework && (
                          <span style={{ 
                            marginLeft: '6px', 
                            color: '#6b7280',
                            fontSize: '10px'
                          }}>
                            ({friction.regulatoryFramework})
                          </span>
                        )}
                      </div>
                      <span style={{ 
                        fontWeight: '600',
                        color: friction.direction === 'negative' ? '#ef4444' : '#22c55e'
                      }}>
                        {friction.direction === 'negative' ? '+' : '-'}{friction.magnitude.toFixed(1)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', fontSize: '11px' }}>
                {entity.dataTypes?.map((dt: string) => (
                  <span key={dt} style={{ background: '#e0f2fe', padding: '2px 6px', borderRadius: '8px' }}>
                    {dt}
                  </span>
                ))}
                {entity.regulatoryCategories?.map((rc: string) => (
                  <span key={rc} style={{ background: '#fef3c7', padding: '2px 6px', borderRadius: '8px' }}>
                    {rc}
                  </span>
                ))}
                {entity.businessModel && (
                  <span style={{ background: '#dbeafe', padding: '2px 6px', borderRadius: '8px' }}>
                    {BUSINESS_MODELS.find(m => m.value === entity.businessModel)?.label || entity.businessModel}
                  </span>
                )}
                {entity.customAttributes && Object.entries(entity.customAttributes).map(([key, value]) => (
                  <span key={key} style={{ background: '#e9d5ff', padding: '2px 6px', borderRadius: '8px' }}>
                    {key}: {typeof value === 'string' ? value : JSON.stringify(value)}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
